import { useState, useEffect, createContext, useContext, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar, Clock, User, LogOut, CheckCircle, AlertCircle,
  Phone, Mail, MapPin, Euro, FileText, Settings, Users, TrendingUp,
  ChevronLeft, ChevronRight, X, Check, Edit2, Save, Send, MessageSquare,
  Plus, Eye, Trash2, Shield, MessageCircle, Share2
} from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// Logo Component
import { Logo } from "./components/Logo";
import SubscriptionAlertBanner from "./components/SubscriptionAlertBanner";
import { ShareDoctorModal } from "./components/ShareDoctorModal";
import { PractitionerCallbacksCard, PhoneSettingsCard } from "./components/CallbackPractitioner";

// ==================== PRACTITIONER CONTEXT ====================
const PractitionerContext = createContext(null);

export const usePractitioner = () => useContext(PractitionerContext);

export const PractitionerProvider = ({ children }) => {
  const [practitioner, setPractitioner] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("doctodays_practitioner_token"));
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem("doctodays_practitioner_token");
    setToken(null);
    setPractitioner(null);
  }, []);

  const fetchPractitioner = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/practitioner/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPractitioner(res.data);
    } catch (err) {
      console.error("Practitioner auth failed:", err);
      logout();
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  useEffect(() => {
    if (token) {
      fetchPractitioner();
    } else {
      setLoading(false);
    }
  }, [token, fetchPractitioner]);

  const login = async (email, password) => {
    const res = await axios.post(`${API_URL}/practitioner/login`, { email, password });
    const accessToken = res.data.access_token;
    localStorage.setItem("doctodays_practitioner_token", accessToken);
    setToken(accessToken);
    setPractitioner(res.data.practitioner);
    return res.data;
  };

  const register = async (data) => {
    const res = await axios.post(`${API_URL}/practitioner/register`, data);
    const accessToken = res.data.access_token;
    localStorage.setItem("doctodays_practitioner_token", accessToken);
    setToken(accessToken);
    setPractitioner(res.data.practitioner);
    return res.data;
  };

  return (
    <PractitionerContext.Provider value={{ practitioner, token, login, register, logout, loading }}>
      {!loading && children}
    </PractitionerContext.Provider>
  );
};

// ==================== PRACTITIONER NAVBAR ====================
export const PractitionerNavbar = () => {
  const { practitioner, logout } = usePractitioner();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/practitioner/login");
  };

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/practitioner/dashboard" className="flex items-center gap-2">
            <Logo />
            <div>
              <span className="text-xl font-bold text-slate-900">Doctodays</span>
              <span className="text-xs text-emerald-600 block -mt-1">Espace Soignant</span>
            </div>
          </Link>

          {practitioner && (
            <nav className="flex items-center gap-6">
              <Link to="/practitioner/dashboard" className="text-slate-600 hover:text-emerald-600 transition-colors flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Planning
              </Link>
              <Link to="/practitioner/messages" className="text-slate-600 hover:text-emerald-600 transition-colors flex items-center gap-2" data-testid="pract-nav-messages">
                <MessageCircle className="w-4 h-4" /> Messages
              </Link>
              <Link to="/practitioner/profile" className="text-slate-600 hover:text-emerald-600 transition-colors flex items-center gap-2">
                <Settings className="w-4 h-4" /> Profil
              </Link>
              <Link to="/practitioner/billing" className="text-slate-600 hover:text-emerald-600 transition-colors flex items-center gap-2" data-testid="pract-nav-billing">
                <Euro className="w-4 h-4" /> Abonnement
              </Link>
              <div className="flex items-center gap-3 pl-4 border-l">
                <span className="text-slate-700 font-medium">Dr. {practitioner.last_name}</span>
                <button onClick={handleLogout} className="p-2 text-slate-500 hover:text-red-500 transition-colors">
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            </nav>
          )}
        </div>
      </div>
    </header>
  );
};

// ==================== EMAIL MODAL ====================
const EmailModal = ({ isOpen, onClose, patient, token }) => {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen && patient) {
      setSubject(`Concernant votre rendez-vous - Doctodays`);
      setMessage(`Bonjour ${patient.name},\n\n`);
      setSent(false);
      setError("");
    }
  }, [isOpen, patient]);

  const handleSend = async () => {
    if (!subject || !message) return;
    if (!patient?.email) {
      setError("L'adresse email du patient n'est pas disponible.");
      return;
    }
    
    setSending(true);
    setError("");
    try {
      await axios.post(`${API_URL}/practitioner/send-email`, {
        patient_id: patient.id,
        subject,
        message
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSent(true);
      setTimeout(() => {
        onClose();
        setSent(false);
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'envoi de l'email");
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="email-modal">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
      >
        <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-emerald-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center">
              <Mail className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Envoyer un email</h3>
              <p className="text-sm text-slate-600">À : {patient?.name} {patient?.email ? `(${patient.email})` : ""}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="close-email-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        {sent ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900">Email envoyé !</h3>
            <p className="text-slate-600">Le patient a été notifié.</p>
          </div>
        ) : (
          <div className="p-4 space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                {error}
              </div>
            )}
            
            {!patient?.email && (
              <div className="bg-amber-50 border border-amber-200 text-amber-700 px-4 py-3 rounded-xl flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                L'adresse email de ce patient n'est pas disponible.
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Objet</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:border-emerald-500 outline-none"
                data-testid="email-subject-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Message</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={6}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:border-emerald-500 outline-none resize-none"
                data-testid="email-message-input"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50"
              >
                Annuler
              </button>
              <button
                onClick={handleSend}
                disabled={sending || !subject || !message || !patient?.email}
                className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="send-email-submit"
              >
                {sending ? "Envoi..." : <><Send className="w-4 h-4" /> Envoyer</>}
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

// ==================== APPOINTMENT DETAIL MODAL ====================
const AppointmentDetailModal = ({ isOpen, onClose, appointment, onStatusChange, onEmailClick }) => {
  if (!isOpen || !appointment) return null;

  const statusColors = {
    confirmed: "bg-emerald-100 text-emerald-700",
    completed: "bg-blue-100 text-blue-700",
    cancelled: "bg-red-100 text-red-700"
  };

  const statusLabels = {
    confirmed: "Confirmé",
    completed: "Terminé",
    cancelled: "Annulé"
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="appointment-detail-modal">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
      >
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">Détails du rendez-vous</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="close-modal-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Patient Info */}
          <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
            <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center">
              <User className="w-7 h-7 text-emerald-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-slate-900 text-lg" data-testid="patient-name">{appointment.patient_name}</h4>
              {appointment.patient_phone && (
                <p className="text-slate-600 flex items-center gap-1 text-sm">
                  <Phone className="w-3 h-3" /> {appointment.patient_phone}
                </p>
              )}
              {appointment.patient_email && (
                <p className="text-slate-600 flex items-center gap-1 text-sm">
                  <Mail className="w-3 h-3" /> {appointment.patient_email}
                </p>
              )}
            </div>
          </div>

          {/* Appointment Details */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500 uppercase">Date</p>
              <p className="font-semibold text-slate-900">
                {new Date(appointment.date).toLocaleDateString("fr-FR", { 
                  weekday: "long", day: "numeric", month: "long" 
                })}
              </p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500 uppercase">Heure</p>
              <p className="font-semibold text-slate-900 text-lg">{appointment.time}</p>
            </div>
          </div>

          {appointment.consultation_type === "telephone" && (
            <div className="p-3 bg-sky-50 rounded-lg border border-sky-100 flex items-center gap-2" data-testid="phone-consultation-badge">
              <Phone className="w-4 h-4 text-sky-600 flex-shrink-0" />
              <p className="text-sky-800 text-sm font-medium">RDV téléphonique — appelez le patient à l'heure du rendez-vous</p>
            </div>
          )}

          {appointment.reason && (
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
              <p className="text-xs text-amber-600 uppercase mb-1">Motif de consultation</p>
              <p className="text-slate-800">{appointment.reason}</p>
            </div>
          )}

          {/* Status */}
          <div className="flex items-center justify-between">
            <span className="text-slate-600">Statut :</span>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[appointment.status]}`}>
              {statusLabels[appointment.status]}
            </span>
          </div>

          {/* Actions */}
          <div className="pt-4 border-t border-slate-100 space-y-2">
            <Link
              to={`/practitioner/messages/${appointment.patient_id}`}
              onClick={onClose}
              className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-colors flex items-center justify-center gap-2 font-medium"
              data-testid="send-chat-message-btn"
            >
              <MessageCircle className="w-5 h-5" /> Envoyer un message au patient
            </Link>
            {appointment.patient_email && (
              <button
                onClick={() => onEmailClick(appointment)}
                className="w-full px-4 py-3 bg-emerald-50 text-emerald-700 rounded-xl hover:bg-emerald-100 transition-colors flex items-center justify-center gap-2 font-medium"
                data-testid="send-email-btn"
              >
                <Mail className="w-5 h-5" /> Envoyer un email au patient
              </button>
            )}
            
            {appointment.status === "confirmed" && (
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => onStatusChange(appointment.id, "completed")}
                  className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors flex items-center justify-center gap-1 text-sm font-medium"
                  data-testid="mark-completed-btn"
                >
                  <Check className="w-4 h-4" /> Terminé
                </button>
                <button
                  onClick={() => onStatusChange(appointment.id, "cancelled")}
                  className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors flex items-center justify-center gap-1 text-sm font-medium"
                  data-testid="cancel-appointment-btn"
                >
                  <X className="w-4 h-4" /> Annuler
                </button>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// ==================== CALENDAR COMPONENT ====================
const OutlookCalendar = ({ appointments, onAppointmentClick, onSlotClick, availableSlots, editMode, onToggleSlot, onSaveSlots }) => {
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(today.setDate(diff));
  });

  const hours = [];
  for (let i = 8; i <= 19; i++) {
    hours.push(`${i.toString().padStart(2, '0')}:00`);
    hours.push(`${i.toString().padStart(2, '0')}:30`);
  }

  const weekDays = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(currentWeekStart);
    date.setDate(currentWeekStart.getDate() + i);
    weekDays.push(date);
  }

  const dayNames = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
  const monthNames = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

  const navigateWeek = (direction) => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(currentWeekStart.getDate() + (direction * 7));
    setCurrentWeekStart(newDate);
  };

  const goToToday = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    setCurrentWeekStart(new Date(today.setDate(diff)));
  };

  const getAppointmentForSlot = (date, time) => {
    const dateStr = date.toISOString().split('T')[0];
    return appointments.find(apt => apt.date === dateStr && apt.time === time);
  };

  const isSlotAvailable = (date, time) => {
    const dateStr = date.toISOString().split('T')[0];
    const slot = availableSlots?.find(s => s.date === dateStr);
    return slot?.times?.includes(time);
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isWeekend = (date) => {
    return date.getDay() === 0 || date.getDay() === 6;
  };

  const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const statusColors = {
    confirmed: "bg-emerald-500",
    completed: "bg-blue-500",
    cancelled: "bg-red-400 opacity-50"
  };

  const handleSlotToggle = (date, time) => {
    if (onToggleSlot && !isPastDate(date) && !isWeekend(date)) {
      const dateStr = date.toISOString().split('T')[0];
      onToggleSlot(dateStr, time);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
      {/* Calendar Header */}
      <div className="p-4 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-bold text-slate-900">
              {monthNames[currentWeekStart.getMonth()]} {currentWeekStart.getFullYear()}
            </h2>
            <button
              onClick={goToToday}
              className="px-3 py-1.5 text-sm bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-colors font-medium"
              data-testid="today-btn"
            >
              Aujourd'hui
            </button>
          </div>
          <div className="flex items-center gap-2">
            {editMode && (
              <button
                onClick={onSaveSlots}
                className="px-4 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium flex items-center gap-2"
                data-testid="save-slots-btn"
              >
                <Save className="w-4 h-4" /> Sauvegarder
              </button>
            )}
            <button
              onClick={() => navigateWeek(-1)}
              className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
              data-testid="prev-week-btn"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => navigateWeek(1)}
              className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
              data-testid="next-week-btn"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
        {editMode && (
          <div className="mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm flex items-center gap-2">
            <Edit2 className="w-4 h-4" />
            <span><strong>Mode édition :</strong> Cliquez sur les créneaux pour les activer/désactiver</span>
          </div>
        )}
      </div>

      {/* Calendar Grid */}
      <div className="overflow-auto max-h-[600px]">
        <div className="min-w-[900px]">
          {/* Days Header */}
          <div className="grid grid-cols-8 border-b border-slate-200 sticky top-0 bg-white z-10">
            <div className="p-3 text-center text-sm font-medium text-slate-500 border-r border-slate-200">
              Heure
            </div>
            {weekDays.map((date, i) => (
              <div
                key={i}
                className={`p-3 text-center border-r border-slate-200 last:border-r-0 ${
                  isWeekend(date) ? 'bg-slate-50' : ''
                } ${isToday(date) ? 'bg-emerald-50' : ''} ${isPastDate(date) ? 'opacity-50' : ''}`}
              >
                <p className={`text-xs font-medium ${isToday(date) ? 'text-emerald-600' : 'text-slate-500'}`}>
                  {dayNames[date.getDay()]}
                </p>
                <p className={`text-lg font-bold ${isToday(date) ? 'text-emerald-600' : 'text-slate-900'}`}>
                  {date.getDate()}
                </p>
              </div>
            ))}
          </div>

          {/* Time Slots */}
          {hours.map((time) => (
            <div key={time} className="grid grid-cols-8 border-b border-slate-100 hover:bg-slate-50/50">
              <div className="p-2 text-center text-xs font-medium text-slate-400 border-r border-slate-200 flex items-center justify-center">
                {time}
              </div>
              {weekDays.map((date, dayIndex) => {
                const appointment = getAppointmentForSlot(date, time);
                const available = isSlotAvailable(date, time);
                const weekend = isWeekend(date);
                const past = isPastDate(date);

                return (
                  <div
                    key={dayIndex}
                    className={`p-1 border-r border-slate-100 last:border-r-0 min-h-[50px] relative ${
                      weekend ? 'bg-slate-50' : ''
                    } ${isToday(date) ? 'bg-emerald-50/30' : ''} ${past ? 'opacity-50' : ''}`}
                  >
                    {appointment ? (
                      <button
                        onClick={() => onAppointmentClick(appointment)}
                        className={`w-full h-full rounded-lg p-2 text-left text-white text-xs transition-all hover:opacity-90 hover:shadow-md ${statusColors[appointment.status]}`}
                        data-testid={`appointment-slot-${date.toISOString().split('T')[0]}-${time}`}
                      >
                        <p className="font-semibold truncate">{appointment.patient_name}</p>
                        {appointment.patient_phone && (
                          <p className="opacity-80 truncate text-[10px]">{appointment.patient_phone}</p>
                        )}
                      </button>
                    ) : editMode && !weekend && !past ? (
                      <button
                        onClick={() => handleSlotToggle(date, time)}
                        className={`w-full h-full rounded-lg transition-all flex items-center justify-center ${
                          available 
                            ? 'bg-emerald-100 border-2 border-emerald-400 hover:bg-emerald-200' 
                            : 'bg-slate-100 border-2 border-dashed border-slate-300 hover:border-emerald-400 hover:bg-emerald-50'
                        }`}
                        data-testid={`slot-toggle-${date.toISOString().split('T')[0]}-${time}`}
                      >
                        {available ? (
                          <Check className="w-4 h-4 text-emerald-600" />
                        ) : (
                          <Plus className="w-4 h-4 text-slate-400" />
                        )}
                      </button>
                    ) : available && !weekend ? (
                      <button
                        onClick={() => onSlotClick(date, time)}
                        className="w-full h-full rounded-lg border-2 border-dashed border-slate-200 hover:border-emerald-400 hover:bg-emerald-50 transition-all flex items-center justify-center opacity-0 hover:opacity-100"
                      >
                        <Plus className="w-4 h-4 text-emerald-500" />
                      </button>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="p-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-6 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-emerald-500"></div>
            <span className="text-slate-600">Confirmé</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-blue-500"></div>
            <span className="text-slate-600">Terminé</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-red-400"></div>
            <span className="text-slate-600">Annulé</span>
          </div>
          {editMode && (
            <>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-emerald-100 border-2 border-emerald-400"></div>
                <span className="text-slate-600">Disponible</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-slate-100 border border-dashed border-slate-300"></div>
                <span className="text-slate-600">Indisponible</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== PRACTITIONER LOGIN PAGE ====================
export const PractitionerLoginPage = () => {
  const { login, register } = usePractitioner();
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    phone: "",
    specialty: "",
    city: "",
    address: "",
    bio: "",
    consultation_fee: 25,
    rpps_number: ""
  });
  const [acceptTerms, setAcceptTerms] = useState(false);

  const specialties = [
    "Médecin généraliste", "Dentiste", "Dermatologue", "Cardiologue",
    "Ophtalmologue", "Kinésithérapeute", "Gynécologue", "Pédiatre",
    "ORL", "Psychiatre", "Rhumatologue", "Neurologue", "Pneumologue"
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (isRegister && !acceptTerms) {
      setError("Vous devez accepter les CGU et la politique de confidentialité.");
      return;
    }

    setLoading(true);

    try {
      if (isRegister) {
        await register({ ...form, accept_terms: true });
      } else {
        await login(form.email, form.password);
      }
      navigate("/practitioner/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left Side */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-emerald-600 to-emerald-700 p-12 flex-col justify-between">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center relative">
            <span className="font-black text-white text-xs tracking-tighter">DD</span>
            <svg className="absolute -top-1 -right-1 w-4 h-4" viewBox="0 0 24 24" fill="white">
              <path d="M18 14H14V18H10V14H6V10H10V6H14V10H18V14Z"/>
            </svg>
          </div>
          <div>
            <span className="text-xl font-bold text-white">Doctodays</span>
            <span className="text-xs text-emerald-200 block">Espace Soignant</span>
          </div>
        </div>
        <div>
          <h2 className="text-4xl font-bold text-white mb-4">
            Gérez votre activité en toute simplicité
          </h2>
          <p className="text-emerald-100 text-lg mb-8">
            Rejoignez Doctodays et bénéficiez d'un outil complet pour gérer vos rendez-vous, votre planning et vos patients.
          </p>
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: <Calendar className="w-5 h-5" />, text: "Calendrier type Outlook" },
              { icon: <Clock className="w-5 h-5" />, text: "Planning flexible" },
              { icon: <Mail className="w-5 h-5" />, text: "Contact patients" },
              { icon: <TrendingUp className="w-5 h-5" />, text: "Statistiques" },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-emerald-100">
                {item.icon}
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="text-emerald-200 text-sm">
          © 2024 Doctodays. Tous droits réservés.
        </div>
      </div>

      {/* Right Side */}
      <div className="flex-1 flex items-center justify-center p-8 overflow-y-auto">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8 justify-center">
            <Logo />
            <span className="text-xl font-bold text-slate-900">Doctodays Pro</span>
          </div>

          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            {isRegister ? "Inscription Soignant" : "Connexion Soignant"}
          </h1>
          <p className="text-slate-600 mb-6">
            {isRegister ? "Créez votre compte professionnel" : "Accédez à votre espace"}
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="practitioner-auth-form">
            {isRegister && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Prénom</label>
                    <input
                      type="text"
                      required
                      value={form.first_name}
                      onChange={(e) => setForm(f => ({ ...f, first_name: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none"
                      data-testid="input-firstname"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Nom</label>
                    <input
                      type="text"
                      required
                      value={form.last_name}
                      onChange={(e) => setForm(f => ({ ...f, last_name: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none"
                      data-testid="input-lastname"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Spécialité</label>
                  <select
                    required
                    value={form.specialty}
                    onChange={(e) => setForm(f => ({ ...f, specialty: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 outline-none"
                    data-testid="input-specialty"
                  >
                    <option value="">Sélectionnez une spécialité</option>
                    {specialties.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Ville</label>
                    <input
                      type="text"
                      required
                      value={form.city}
                      onChange={(e) => setForm(f => ({ ...f, city: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 outline-none"
                      data-testid="input-city"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Téléphone</label>
                    <input
                      type="tel"
                      required
                      value={form.phone}
                      onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 outline-none"
                      data-testid="input-phone"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Adresse du cabinet</label>
                  <input
                    type="text"
                    required
                    value={form.address}
                    onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 outline-none"
                    data-testid="input-address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Tarif consultation (€)</label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={form.consultation_fee}
                    onChange={(e) => setForm(f => ({ ...f, consultation_fee: parseInt(e.target.value) }))}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 outline-none"
                    data-testid="input-fee"
                  />
                </div>

                {/* RPPS Field - Optional */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Shield className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-emerald-800 mb-1">
                        Numéro RPPS <span className="text-emerald-600 font-normal">(optionnel)</span>
                      </label>
                      <p className="text-xs text-emerald-600 mb-2">
                        Renseignez votre numéro RPPS pour une validation automatique de votre compte
                      </p>
                      <input
                        type="text"
                        value={form.rpps_number}
                        onChange={(e) => setForm(f => ({ ...f, rpps_number: e.target.value.replace(/\D/g, '').slice(0, 11) }))}
                        placeholder="Ex: 10101234567 (11 chiffres)"
                        className="w-full px-4 py-3 rounded-xl border border-emerald-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none bg-white"
                        data-testid="input-rpps"
                        maxLength={11}
                      />
                      {form.rpps_number && form.rpps_number.length === 11 && (
                        <p className="text-xs text-emerald-700 mt-1 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> Format valide - sera vérifié à l'inscription
                        </p>
                      )}
                      {form.rpps_number && form.rpps_number.length > 0 && form.rpps_number.length < 11 && (
                        <p className="text-xs text-amber-600 mt-1">
                          {11 - form.rpps_number.length} chiffres restants
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email professionnel</label>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none"
                data-testid="input-email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Mot de passe</label>
              <input
                type="password"
                required
                value={form.password}
                onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none"
                data-testid="input-password"
              />
            </div>

            {isRegister && (
              <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer" data-testid="prac-consent-wrapper">
                <input
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                  data-testid="prac-consent-checkbox"
                />
                <span>
                  J'accepte les{" "}
                  <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline font-medium">
                    Conditions Générales d'Utilisation
                  </Link>
                  {" "}et la{" "}
                  <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline font-medium">
                    politique de confidentialité
                  </Link>
                  .<span className="text-red-500"> *</span>
                </span>
              </label>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-emerald-600 text-white py-3 rounded-xl font-semibold hover:bg-emerald-700 transition-all disabled:opacity-50"
              data-testid="submit-btn"
            >
              {loading ? "Chargement..." : (isRegister ? "Créer mon compte" : "Se connecter")}
            </button>

            {!isRegister && (
              <div className="text-center">
                <Link
                  to="/practitioner/forgot-password"
                  className="text-sm text-slate-500 hover:text-emerald-600"
                  data-testid="forgot-password-link"
                >
                  Mot de passe oublié ?
                </Link>
              </div>
            )}
          </form>

          <p className="text-center mt-6 text-slate-600">
            {isRegister ? "Déjà inscrit ?" : "Pas encore de compte ?"}{" "}
            <button
              onClick={() => { setIsRegister(!isRegister); setError(""); }}
              className="text-emerald-600 font-medium hover:underline"
              data-testid="toggle-auth"
            >
              {isRegister ? "Se connecter" : "S'inscrire"}
            </button>
          </p>

          <div className="mt-8 pt-6 border-t text-center">
            <Link to="/" className="text-slate-500 text-sm hover:text-emerald-600">
              ← Retour au site patient
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== PRACTITIONER DASHBOARD ====================
export const PractitionerDashboardPage = () => {
  const { practitioner, token } = usePractitioner();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [slots, setSlots] = useState([]);
  const [editedSlots, setEditedSlots] = useState([]);
  const [stats, setStats] = useState({ total: 0, upcoming: 0, today: 0, cancelled: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailPatient, setEmailPatient] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  // Auto-open the share modal when arriving from mobile deep-link (?openCardsOrder=1)
  useEffect(() => {
    try {
      if (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("openCardsOrder") === "1") {
        setShareOpen(true);
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (!practitioner) {
      navigate("/practitioner/login");
      return;
    }
    fetchData();
  }, [practitioner, navigate]);

  const fetchData = async () => {
    try {
      const [aptsRes, statsRes, slotsRes] = await Promise.all([
        axios.get(`${API_URL}/practitioner/appointments`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/practitioner/appointments/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/practitioner/slots`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setAppointments(aptsRes.data);
      setStats(statsRes.data);
      const fetchedSlots = slotsRes.data.slots || [];
      setSlots(fetchedSlots);
      setEditedSlots(JSON.parse(JSON.stringify(fetchedSlots)));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (aptId, newStatus) => {
    try {
      await axios.patch(`${API_URL}/practitioner/appointments/${aptId}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedAppointment(null);
      fetchData();
    } catch (err) {
      alert("Erreur lors de la mise à jour");
    }
  };

  const handleAppointmentClick = (apt) => {
    if (!editMode) {
      setSelectedAppointment(apt);
    }
  };

  const handleEmailClick = (apt) => {
    setSelectedAppointment(null);
    setEmailPatient({
      id: apt.patient_id,
      name: apt.patient_name,
      email: apt.patient_email || ""
    });
    setEmailModalOpen(true);
  };

  const handleSlotClick = (date, time) => {
    console.log("Slot clicked:", date, time);
  };

  const handleToggleSlot = (dateStr, time) => {
    setEditedSlots(prev => {
      const newSlots = [...prev];
      const existingSlotIndex = newSlots.findIndex(s => s.date === dateStr);
      
      if (existingSlotIndex >= 0) {
        const slot = { ...newSlots[existingSlotIndex] };
        if (slot.times.includes(time)) {
          slot.times = slot.times.filter(t => t !== time);
        } else {
          slot.times = [...slot.times, time].sort();
        }
        newSlots[existingSlotIndex] = slot;
      } else {
        const date = new Date(dateStr);
        const dayNames = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
        newSlots.push({
          date: dateStr,
          day_name: dayNames[date.getDay()],
          times: [time]
        });
      }
      
      return newSlots;
    });
  };

  const handleSaveSlots = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/practitioner/slots`, editedSlots.map(s => ({
        date: s.date,
        times: s.times
      })), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSlots(editedSlots);
      setEditMode(false);
      alert("Disponibilités mises à jour !");
    } catch (err) {
      alert("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const toggleEditMode = () => {
    if (editMode) {
      setEditedSlots(JSON.parse(JSON.stringify(slots)));
    }
    setEditMode(!editMode);
  };

  if (!practitioner) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <PractitionerNavbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <SubscriptionAlertBanner token={token} />
        {/* Header */}
        <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              Bonjour, Dr. {practitioner.last_name} !
            </h1>
            <p className="text-slate-600">{practitioner.specialty}</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {practitioner.is_verified && (
              <button
                onClick={() => setShareOpen(true)}
                className="px-4 py-2 bg-white border border-emerald-200 text-emerald-700 rounded-lg font-medium hover:bg-emerald-50 transition-colors flex items-center gap-2"
                data-testid="practitioner-share-card-btn"
              >
                <Share2 className="w-4 h-4" /> Partager ma fiche
              </button>
            )}
            <button
              onClick={toggleEditMode}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                editMode 
                  ? 'bg-slate-200 text-slate-700 hover:bg-slate-300' 
                  : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
              }`}
              data-testid="toggle-edit-mode"
            >
              {editMode ? (
                <><X className="w-4 h-4" /> Annuler</>
              ) : (
                <><Edit2 className="w-4 h-4" /> Modifier les disponibilités</>
              )}
            </button>
            <Link
              to="/practitioner/planning"
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors flex items-center gap-2"
            >
              <Settings className="w-4 h-4" /> Planning avancé
            </Link>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: "RDV aujourd'hui", value: stats.today, color: "emerald", icon: <Calendar className="w-5 h-5" /> },
            { label: "À venir", value: stats.upcoming, color: "blue", icon: <Clock className="w-5 h-5" /> },
            { label: "Total", value: stats.total, color: "slate", icon: <Users className="w-5 h-5" /> },
            { label: "Annulés", value: stats.cancelled, color: "red", icon: <X className="w-5 h-5" /> },
          ].map((stat, i) => (
            <div key={i} className="bg-white rounded-xl p-5 border border-slate-100 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className={`text-${stat.color}-600`}>{stat.icon}</span>
              </div>
              <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
              <p className="text-slate-500 text-sm">{stat.label}</p>
            </div>
          ))}
        </div>

        <PractitionerCallbacksCard token={token} />

        {/* Calendar */}
        {loading ? (
          <div className="bg-white rounded-xl p-8 text-center">
            <div className="animate-spin w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : (
          <OutlookCalendar
            appointments={appointments}
            availableSlots={editMode ? editedSlots : slots}
            onAppointmentClick={handleAppointmentClick}
            onSlotClick={handleSlotClick}
            editMode={editMode}
            onToggleSlot={handleToggleSlot}
            onSaveSlots={handleSaveSlots}
          />
        )}
      </div>

      {/* Modals */}
      <AnimatePresence>
        {selectedAppointment && (
          <AppointmentDetailModal
            isOpen={!!selectedAppointment}
            onClose={() => setSelectedAppointment(null)}
            appointment={selectedAppointment}
            onStatusChange={updateStatus}
            onEmailClick={handleEmailClick}
          />
        )}
      </AnimatePresence>

      <EmailModal
        isOpen={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        patient={emailPatient}
        token={token}
      />

      <ShareDoctorModal
        doctor={practitioner}
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        apiBase={process.env.REACT_APP_BACKEND_URL}
        siteBase={typeof window !== 'undefined' ? window.location.origin : ''}
        isOwner={true}
        practitionerToken={token}
      />
    </div>
  );
};

// ==================== PRACTITIONER PLANNING PAGE ====================
export const PractitionerPlanningPage = () => {
  const { practitioner, token } = usePractitioner();
  const navigate = useNavigate();
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const defaultTimes = ["08:00", "08:30", "09:00", "09:30", "10:00", "10:30", "11:00", "11:30", "12:00", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30", "17:00", "17:30", "18:00", "18:30", "19:00"];

  useEffect(() => {
    if (!practitioner) {
      navigate("/practitioner/login");
      return;
    }
    fetchSlots();
  }, [practitioner, navigate]);

  const fetchSlots = async () => {
    try {
      const res = await axios.get(`${API_URL}/practitioner/slots`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSlots(res.data.slots || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const generateWeekSlots = () => {
    const newSlots = [];
    const dayNames = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
    
    for (let i = 0; i < 14; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      const dateStr = date.toISOString().split("T")[0];
      const dayOfWeek = date.getDay();
      
      if (dayOfWeek === 0 || dayOfWeek === 6) continue;
      
      const existingSlot = slots.find(s => s.date === dateStr);
      newSlots.push({
        date: dateStr,
        day_name: dayNames[dayOfWeek],
        times: existingSlot ? existingSlot.times : [...defaultTimes]
      });
    }
    setSlots(newSlots);
  };

  const toggleTime = (dateIndex, time) => {
    setSlots(prev => {
      const updated = [...prev];
      const slot = { ...updated[dateIndex] };
      if (slot.times.includes(time)) {
        slot.times = slot.times.filter(t => t !== time);
      } else {
        slot.times = [...slot.times, time].sort();
      }
      updated[dateIndex] = slot;
      return updated;
    });
  };

  const saveSlots = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/practitioner/slots`, slots.map(s => ({
        date: s.date,
        times: s.times
      })), {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert("Planning sauvegardé !");
    } catch (err) {
      alert("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  if (!practitioner) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <PractitionerNavbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Gestion du planning</h1>
            <p className="text-slate-600">Définissez vos créneaux de disponibilité</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={generateWeekSlots}
              className="px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            >
              Générer 2 semaines
            </button>
            <button
              onClick={saveSlots}
              disabled={saving}
              className="px-6 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? "Sauvegarde..." : "Sauvegarder"}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="bg-white rounded-xl p-8 text-center">
            <div className="animate-spin w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : slots.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center border border-slate-100">
            <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Aucun créneau défini</h3>
            <p className="text-slate-500 mb-4">Cliquez sur "Générer 2 semaines" pour créer votre planning</p>
            <button
              onClick={generateWeekSlots}
              className="px-6 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors"
            >
              Générer le planning
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {slots.map((slot, dateIndex) => (
              <div key={slot.date} className="bg-white rounded-xl border border-slate-100 overflow-hidden">
                <div className="p-4 border-b border-slate-100 bg-slate-50">
                  <h3 className="font-semibold text-slate-900">
                    {slot.day_name} {new Date(slot.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}
                  </h3>
                  <p className="text-sm text-slate-500">{slot.times.length} créneaux disponibles</p>
                </div>
                <div className="p-4">
                  <div className="flex flex-wrap gap-2">
                    {defaultTimes.map(time => (
                      <button
                        key={time}
                        onClick={() => toggleTime(dateIndex, time)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          slot.times.includes(time)
                            ? "bg-emerald-600 text-white"
                            : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                        }`}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== PRACTITIONER PROFILE PAGE ====================
export const PractitionerProfilePage = () => {
  const { practitioner, token } = usePractitioner();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [currentPhotoUrl, setCurrentPhotoUrl] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const [form, setForm] = useState({
    bio: "",
    consultation_fee: 25,
    phone: "",
    address: ""
  });

  useEffect(() => {
    if (!practitioner) {
      navigate("/practitioner/login");
      return;
    }
    setForm({
      bio: practitioner.bio || "",
      consultation_fee: practitioner.consultation_fee || 25,
      phone: practitioner.phone || "",
      address: practitioner.address || ""
    });
    setCurrentPhotoUrl(practitioner.photo_url || "");
  }, [practitioner, navigate]);

  const saveProfile = async () => {
    try {
      await axios.put(`${API_URL}/practitioner/profile`, null, {
        params: form,
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditing(false);
      alert("Profil mis à jour !");
    } catch (err) {
      alert("Erreur lors de la mise à jour");
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Veuillez sélectionner une image");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("L'image ne doit pas dépasser 5 Mo");
      return;
    }

    setUploadingPhoto(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64Data = reader.result;
          const res = await axios.post(`${API_URL}/practitioner/upload-photo`, {
            photo_data: base64Data
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          // Update local state with new photo URL
          const newPhotoUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.photo_url}?t=${Date.now()}`;
          setCurrentPhotoUrl(newPhotoUrl);
          alert("Photo mise à jour !");
        } catch (err) {
          alert(err.response?.data?.detail || "Erreur lors de l'upload");
        } finally {
          setUploadingPhoto(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      alert("Erreur lors de la lecture du fichier");
      setUploadingPhoto(false);
    }
  };

  if (!practitioner) return null;

  // Determine photo URL to display
  const displayPhotoUrl = currentPhotoUrl.startsWith("/api") 
    ? `${process.env.REACT_APP_BACKEND_URL}${currentPhotoUrl}`
    : currentPhotoUrl;

  return (
    <div className="min-h-screen bg-slate-50">
      <PractitionerNavbar />
      
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <h1 className="text-2xl font-bold text-slate-900">Mon profil</h1>
          {practitioner.is_verified && (
            <button
              onClick={() => setShareOpen(true)}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors flex items-center gap-2 shadow-sm"
              data-testid="practitioner-share-card-btn-profile"
            >
              <Share2 className="w-4 h-4" /> Partager ma fiche
            </button>
          )}
        </div>

        <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
          <div className="p-6 border-b border-slate-100 bg-gradient-to-r from-emerald-500 to-emerald-600">
            <div className="flex items-center gap-4">
              <div className="relative group">
                <img
                  src={displayPhotoUrl || "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=400"}
                  alt={`Dr. ${practitioner.last_name}`}
                  className="w-24 h-24 rounded-xl object-cover border-2 border-white shadow-lg"
                />
                <label className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-xl opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                    disabled={uploadingPhoto}
                    data-testid="photo-upload-input"
                  />
                  {uploadingPhoto ? (
                    <div className="animate-spin w-6 h-6 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <div className="text-white text-center">
                      <Edit2 className="w-5 h-5 mx-auto mb-1" />
                      <span className="text-xs">Modifier</span>
                    </div>
                  )}
                </label>
              </div>
              <div className="text-white">
                <h2 className="text-xl font-bold">Dr. {practitioner.first_name} {practitioner.last_name}</h2>
                <p className="text-emerald-100">{practitioner.specialty}</p>
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-yellow-300">★</span>
                  <span>{practitioner.rating || "Nouveau"}</span>
                  <span className="text-emerald-200">({practitioner.review_count} avis)</span>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">Informations</h3>
              <button
                onClick={() => editing ? saveProfile() : setEditing(true)}
                className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700"
                data-testid="edit-profile-btn"
              >
                {editing ? <Save className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
                {editing ? "Sauvegarder" : "Modifier"}
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1">Email</label>
                <p className="text-slate-900">{practitioner.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1">Téléphone</label>
                {editing ? (
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    data-testid="edit-phone"
                  />
                ) : (
                  <p className="text-slate-900">{practitioner.phone}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1">Ville</label>
                <p className="text-slate-900">{practitioner.city}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1">Tarif consultation</label>
                {editing ? (
                  <input
                    type="number"
                    value={form.consultation_fee}
                    onChange={(e) => setForm(f => ({ ...f, consultation_fee: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    data-testid="edit-fee"
                  />
                ) : (
                  <p className="text-slate-900">{practitioner.consultation_fee} €</p>
                )}
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-500 mb-1">Adresse</label>
                {editing ? (
                  <input
                    type="text"
                    value={form.address}
                    onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    data-testid="edit-address"
                  />
                ) : (
                  <p className="text-slate-900">{practitioner.address}</p>
                )}
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-500 mb-1">Biographie</label>
                {editing ? (
                  <textarea
                    value={form.bio}
                    onChange={(e) => setForm(f => ({ ...f, bio: e.target.value }))}
                    rows={4}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    data-testid="edit-bio"
                  />
                ) : (
                  <p className="text-slate-900">{practitioner.bio || "Non renseignée"}</p>
                )}
              </div>
            </div>

            {/* RPPS Status */}
            {practitioner.rpps_number && (
              <div className={`rounded-xl p-4 flex items-start gap-3 ${practitioner.rpps_verified ? 'bg-emerald-50 border border-emerald-200' : 'bg-slate-50 border border-slate-200'}`}>
                <Shield className={`w-5 h-5 mt-0.5 ${practitioner.rpps_verified ? 'text-emerald-600' : 'text-slate-500'}`} />
                <div>
                  <p className={`font-medium ${practitioner.rpps_verified ? 'text-emerald-800' : 'text-slate-700'}`}>
                    Numéro RPPS : {practitioner.rpps_number}
                  </p>
                  <p className={`text-sm ${practitioner.rpps_verified ? 'text-emerald-600' : 'text-slate-500'}`}>
                    {practitioner.rpps_verified ? '✓ Vérifié auprès de l\'Annuaire Santé' : 'En attente de vérification'}
                  </p>
                </div>
              </div>
            )}

            {/* GDPR data export (article 20) */}
            <div className="mt-4 bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-center gap-3 flex-wrap" data-testid="prac-gdpr-banner">
              <Share2 className="w-5 h-5 text-emerald-600 shrink-0" />
              <div className="flex-1 min-w-[180px]">
                <p className="font-medium text-slate-900 text-sm">Exporter mes données (RGPD)</p>
                <p className="text-xs text-slate-600">Récupérez toutes vos données DoctoDays au format JSON.</p>
              </div>
              <button
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API_URL}/practitioner/account/me/export`, {
                      headers: { Authorization: `Bearer ${token}` },
                      responseType: "blob",
                    });
                    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = `doctodays-export-${practitioner.id?.slice(0, 8) || "data"}.zip`;
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                  } catch {
                    alert("Téléchargement impossible pour le moment. Réessayez plus tard.");
                  }
                }}
                className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors"
                data-testid="prac-gdpr-export-btn"
              >
                Exporter
              </button>
            </div>
            {/* Verification Status */}
            {practitioner.is_verified ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5" />
                <div>
                  <p className="font-medium text-emerald-800">Compte vérifié</p>
                  <p className="text-sm text-emerald-600">
                    Votre profil est visible par les patients.
                    {practitioner.rpps_verified && ' (Validé automatiquement via RPPS)'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Compte en attente de vérification</p>
                  <p className="text-sm text-amber-600">
                    Votre profil sera visible après validation par notre équipe.
                    {!practitioner.rpps_number && ' Ajoutez votre numéro RPPS pour une validation automatique.'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        <PhoneSettingsCard token={token} />
      </div>

      <ShareDoctorModal
        doctor={practitioner}
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        apiBase={process.env.REACT_APP_BACKEND_URL}
        siteBase={typeof window !== 'undefined' ? window.location.origin : ''}
        isOwner={true}
        practitionerToken={token}
      />
    </div>
  );
};

// ==================== FORGOT PASSWORD PAGE (PRACTITIONER) ====================
export const PractitionerForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await axios.post(`${API_URL}/auth/forgot-password`, {
        email,
        account_type: "practitioner"
      });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-emerald-600 to-emerald-800 p-12 flex-col justify-center">
        <Logo size="large" />
        <h1 className="text-4xl font-bold text-white mt-8">Espace Professionnel</h1>
        <p className="text-emerald-100 text-lg mt-4">
          Réinitialisez votre mot de passe pour accéder à votre espace Doctodays Pro.
        </p>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4 lg:hidden">
              <Logo size="large" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Mot de passe oublié</h1>
            <p className="text-slate-600 mt-2">
              {sent ? "Email envoyé !" : "Entrez votre email professionnel"}
            </p>
          </div>

          {sent ? (
            <div className="text-center space-y-6">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                <Mail className="w-8 h-8 text-emerald-600" />
              </div>
              <div>
                <p className="text-slate-600">
                  Si un compte existe avec l'adresse <strong>{email}</strong>, vous recevrez un email avec un lien de réinitialisation.
                </p>
              </div>
              <Link
                to="/practitioner/login"
                className="inline-block px-6 py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700"
              >
                Retour à la connexion
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email professionnel</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 outline-none"
                  placeholder="votre@email.com"
                  data-testid="forgot-email"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-600 text-white py-3 rounded-xl font-semibold hover:bg-emerald-700 transition-all disabled:opacity-50"
                data-testid="forgot-submit"
              >
                {loading ? "Envoi..." : "Envoyer le lien de réinitialisation"}
              </button>

              <div className="text-center">
                <Link to="/practitioner/login" className="text-slate-600 hover:text-emerald-600 text-sm">
                  ← Retour à la connexion
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== RESET PASSWORD PAGE (PRACTITIONER) ====================
export const PractitionerResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      return;
    }

    if (password.length < 6) {
      setError("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      setTimeout(() => navigate("/practitioner/login"), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Lien invalide</h1>
          <p className="text-slate-600 mb-6">Ce lien de réinitialisation est invalide ou a expiré.</p>
          <Link to="/practitioner/forgot-password" className="text-emerald-600 hover:underline">
            Demander un nouveau lien
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Logo size="large" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">
            {success ? "Mot de passe réinitialisé !" : "Nouveau mot de passe"}
          </h1>
        </div>

        {success ? (
          <div className="text-center space-y-6">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <p className="text-slate-600">
              Votre mot de passe a été modifié avec succès.<br />
              Redirection vers la connexion...
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Nouveau mot de passe</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 outline-none"
                placeholder="••••••••"
                minLength={6}
                data-testid="new-password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Confirmer le mot de passe</label>
              <input
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 outline-none"
                placeholder="••••••••"
                minLength={6}
                data-testid="confirm-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-emerald-600 text-white py-3 rounded-xl font-semibold hover:bg-emerald-700 transition-all disabled:opacity-50"
              data-testid="reset-submit"
            >
              {loading ? "Réinitialisation..." : "Réinitialiser le mot de passe"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default {
  PractitionerProvider,
  PractitionerLoginPage,
  PractitionerDashboardPage,
  PractitionerPlanningPage,
  PractitionerProfilePage,
  PractitionerForgotPasswordPage,
  PractitionerResetPasswordPage
};
